import { Link } from "react-router-dom";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DownloadIcon from "@mui/icons-material/Download";
import { createTheme } from "@mui/material/styles";
import { useSnackbar } from "notistack";
import { Button, Tooltip } from "@mui/material";

import { useState } from "react";
import { Dialog, DialogTitle, DialogContent, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

// Style mappings for filled and outline buttons
const variantStyles = {
  add: {
    filled: "text-white bg-blue-600 hover:bg-blue-700",
    outline: "text-blue-600 border border-blue-600 hover:bg-gray-100",
  },
  edit: {
    filled: "text-white bg-green-600 hover:bg-green-700",
    outline: "text-green-700 border border-green-700 hover:bg-gray-100",
  },
  delete: {
    filled: "text-white bg-red-600 hover:bg-red-700",
    outline: "text-red-600 border border-red-600 hover:bg-gray-100",
  },
  search: {
    filled: "text-white bg-blue-600 hover:bg-blue-700",
    outline:
      "text-blue-600 border border-blue-600 hover:bg-blue-50 bg-transparent",
  },
  copy: {
    filled: "text-white bg-purple-600 hover:bg-purple-700",
    outline: "text-purple-600 border border-purple-600 hover:bg-gray-100",
  },
  cancel: {
    filled: "text-white bg-gray-500 hover:bg-gray-600",
    outline: "text-blue-600 border border-blue-600 hover:bg-blue-100",
  },
  upload: {
    filled: "text-white bg-indigo-600 hover:bg-indigo-700",
    outline: "text-indigo-600 border border-indigo-600 hover:bg-gray-100",
  },
  approve: {
    filled: "bg-green-600 text-white hover:bg-green-700",
    outline: "border border-green-600 text-green-600 hover:bg-green-50",
  },
  reject: {
    filled: "bg-red-600 text-white hover:bg-red-700",
    outline: "border border-red-600 text-red-600 hover:bg-red-50",
  },
};

let customColor = null;
// Base button
const CreateButton = ({
  to,
  label,
  id,
  icon,
  variant = "add",
  outline = false,
  onClick,
  disabled = false,
  className = "",
}) => {
  const baseClasses =
    "btn font-sans text-md font-medium py-2 px-3 rounded inline-flex items-center justify-center gap-2 capitalize " +
    className;
  const useCustom = !!customColor;
  const colorClasses = useCustom
    ? ""
    : variantStyles[variant]?.[outline ? "outline" : "filled"] ||
      variantStyles["add"].filled;

  // Add conditional styling for disabled state
  const disabledClasses = disabled
    ? "opacity-50 cursor-not-allowed pointer-events-none"
    : "";

  const finalClass = `${baseClasses} ${colorClasses} ${disabledClasses}`;

  const customStyle = useCustom
    ? outline
      ? {
          color: customColor,
          border: `1px solid ${customColor}`,
          backgroundColor: "transparent",
        }
      : {
          color: "#fff",
          backgroundColor: customColor,
        }
    : {};
  const button = (
    <button
      id={id}
      className={finalClass}
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={customStyle}
    >
      {icon && <span>{icon}</span>}
      {label}
    </button>
  );

  return to ? <Link to={to}>{button}</Link> : button;
};

// Named buttons for direct import
export const AddButton = (props) => <CreateButton {...props} />;

export const ApproveButton = (props) => (
  <CreateButton
    {...props}
    variant="approve"
  />
);

export const RejectButton = (props) => (
  <CreateButton
    {...props}
    variant="reject"
  />
);


export const SaveAndUpdate = (props) => (
  <CreateButton {...props} variant="add" />
);

export const EditButton = (props) => (
  <CreateButton
    {...props}
    variant="edit"
    icon={<EditIcon fontSize="small" />}
  />
);

export const SearchToggleButton = ({ searched, onSearch, onClear }) => (
  <CreateButton
    id="search-toggle-btn"
    variant={searched ? "delete" : "search"}
    outline={false}
    label={searched ? "Reset" : "Search"}
    icon={
      searched ? (
        <RestartAltIcon fontSize="small" />
      ) : (
        <SearchIcon fontSize="small" />
      )
    }
    onClick={() => {
      searched ? onClear() : onSearch();
    }}
  />
);

export const DeleteButton = (props) => (
  <CreateButton
    {...props}
    variant="delete"
    icon={<DeleteIcon fontSize="small" />}
  />
);

export const SearchButton = (props) => (
  <CreateButton
    {...props}
    variant="search"
    icon={<SearchIcon fontSize="small" />}
  />
);

export const CopyButton = (props) => (
  <CreateButton
    {...props}
    variant="copy"
    icon={<ContentCopyIcon fontSize="small" />}
  />
);

export const CancelButton = (props) => (
  <CreateButton
    {...props}
    variant="cancel"
    // icon={<CancelIcon fontSize="small" />}
  />
);

export const UploadButton = ({
  label = "Upload",
  onChange,
  outline = false,
  fileUrl = "", // Pass the uploaded file URL here
  showFile = false,
  className = "",
  onRemove,
  tooltipLabel = "",
  downloadFile = false,
  ...props
}) => {
  const baseClasses =
    "btn font-sans text-md font-medium py-2 px-3 rounded inline-flex items-center justify-center gap-2 cursor-pointer";
  const colorClasses = outline
    ? "text-indigo-600 border border-indigo-600 hover:bg-gray-100"
    : "text-white bg-indigo-600 hover:bg-indigo-700";
  const finalClass = `${baseClasses} ${colorClasses} ${className}`;

  const Wrapper = ({ children }) =>
    tooltipLabel ? (
      <Tooltip title={tooltipLabel} arrow>
        <span>{children}</span>
      </Tooltip>
    ) : (
      children
    );

  // DOWNLOAD HANDLER
  const handleDownload = () => {
    if (!fileUrl) return;

    fetch(fileUrl)
      .then((response) => response.blob())
      .then((blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;

        // Extract filename from URL
        const fileName = fileUrl.split("/").pop() || "downloaded_file";
        link.setAttribute("download", fileName);

        document.body.appendChild(link);
        link.click();

        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      })
      .catch((err) => console.error("Download error:", err));
  };

  return (
    <div className="flex items-center gap-3">
      {/* Upload button */}
      <Wrapper>
        <label className={finalClass}>
          <CloudUploadIcon fontSize="small" />
          {label}
          <input
            type="file"
            onChange={onChange}
            className="hidden"
            {...props}
          />
        </label>
      </Wrapper>

      {/* View file icon styled same as label */}
      {fileUrl && (
        <button
          type="button"
          onClick={() => window.open(fileUrl, "_blank", "noopener,noreferrer")}
          className={finalClass}
          title="View file"
        >
          <VisibilityIcon fontSize="small" />
        </button>
      )}

      {/* DOWNLOAD BUTTON - NEW */}
      {fileUrl && downloadFile && (
        <button
          type="button"
          onClick={handleDownload}
          className={finalClass}
          title="Download file"
        >
          <DownloadIcon fontSize="small" />
        </button>
      )}

      {/* Remove file */}
      {fileUrl && onRemove && (
        <button
          type="button"
          onClick={onRemove}
          className={`${finalClass} bg-red-500`}
          title="Remove file"
        >
          <CloseIcon fontSize="small" />
        </button>
      )}
    </div>
  );
};

export const ModalButton = ({
  buttonLabel = "Open Modal",
  modalTitle = "Modal Title",
  children,
}) => {
  const [open, setOpen] = useState(false);

  const handleToggle = () => setOpen(!open);

  return (
    <>
      <AddButton onClick={handleToggle} label={buttonLabel} />

      <Dialog open={open} onClose={handleToggle} maxWidth="sm" fullWidth>
        <DialogTitle className="flex justify-between items-center">
          <span className="text-lg font-semibold">{modalTitle}</span>
          <IconButton onClick={handleToggle} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers className="space-y-4">
          {children}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default function CButton(props) {
  const { enqueueSnackbar } = useSnackbar();
  const theme = createTheme({
    palette: {
      primary: {
        light: "#757ce8",
        main: "#3f50b5",
        dark: "#002884",
        contrastText: "#fff",
      },
      secondary: {
        light: "#ff7961",
        main: "#f44336",
        dark: "#ba000d",
        contrastText: "#000",
      },
    },
  });
  return (
    <Button
      variant="contained"
      sx={{ m: 0 }}
      style={theme.secondary}
      onClick={() => {
        enqueueSnackbar(props.message, {
          variant: props.variant,
        });
      }}
      {...props}
    >
      {props.label}
    </Button>
  );
}
